import { DateTime } from 'luxon';
import { parseISO } from 'date-fns';

export const ZNY = 'America/New_York';
export const ZLONDON = 'Europe/London';

function toMillis(input) {
  if (input == null) return NaN;
  const d = input instanceof Date ? input : typeof input === 'string' ? parseISO(input) : null;
  if (!d || isNaN(d.getTime())) return NaN;
  return d.getTime();
}

/** Tight "3:05pm" / "3pm" — lowercase am/pm, no space before am/pm */
export function utcToTightInZone(input, zone) {
  const ms = toMillis(input);
  if (isNaN(ms)) return '';
  const s = DateTime.fromMillis(ms, { zone: 'utc' })
    .setZone(zone)
    .toFormat('h:mma')
    .toLowerCase();
  return s
    .replace(/:00(?=[ap]m\b)/, '')
    .replace(/\s+([ap]m)\b/g, '$1');
}

export function utcToTightNy(input) {
  return utcToTightInZone(input, ZNY);
}

export function utcToTightLondon(input) {
  return utcToTightInZone(input, ZLONDON);
}

export function utcLondonZoneAbbr(input) {
  const ms = toMillis(input);
  if (isNaN(ms)) return '';
  return DateTime.fromMillis(ms, { zone: 'utc' }).setZone(ZLONDON).toFormat('ZZZZ');
}

export function nyClockHour(input) {
  const ms = toMillis(input);
  if (isNaN(ms)) return NaN;
  return DateTime.fromMillis(ms, { zone: 'utc' }).setZone(ZNY).hour;
}

export function nyClockHourMinute(input) {
  const ms = toMillis(input);
  if (isNaN(ms)) return NaN;
  const dt = DateTime.fromMillis(ms, { zone: 'utc' }).setZone(ZNY);
  return dt.hour * 60 + dt.minute;
}

/** Week/month cell: treat calendar (y,m,d) as a US Eastern date, hour = 0–23 local NY */
export function nySlotUtcRange(dayDate, hour24) {
  const y = dayDate.getFullYear();
  const mo = dayDate.getMonth() + 1;
  const d = dayDate.getDate();
  const start = DateTime.fromObject(
    { year: y, month: mo, day: d, hour: hour24, minute: 0, second: 0, millisecond: 0 },
    { zone: ZNY }
  ).toUTC();
  const end = start.plus({ hours: 1 });
  return { start: start.toJSDate(), end: end.toJSDate() };
}

export function nyDayUtcRange(dayDate) {
  const y = dayDate.getFullYear();
  const mo = dayDate.getMonth() + 1;
  const d = dayDate.getDate();
  const start = DateTime.fromObject({ year: y, month: mo, day: d }, { zone: ZNY }).startOf('day').toUTC();
  const end = DateTime.fromObject({ year: y, month: mo, day: d }, { zone: ZNY }).endOf('day').toUTC();
  return { start: start.toJSDate(), end: end.toJSDate() };
}

export function formatNyWeekdayMonthDay(input) {
  const ms = toMillis(input);
  if (isNaN(ms)) return '';
  return DateTime.fromMillis(ms, { zone: 'utc' }).setZone(ZNY).toFormat('EEEE, MMMM d');
}

export function utcNyFormatMmmDYyyy(input) {
  const ms = toMillis(input);
  if (isNaN(ms)) return '';
  return DateTime.fromMillis(ms, { zone: 'utc' }).setZone(ZNY).toFormat('MMM d, yyyy');
}

/** e.g. "Mar 27, 2026 3:05pm ET" */
export function utcNyFormatDateTimeEtLabel(input) {
  const ms = toMillis(input);
  if (isNaN(ms)) return '';
  const s = DateTime.fromMillis(ms, { zone: 'utc' })
    .setZone(ZNY)
    .toFormat('MMM d, yyyy h:mma')
    .toLowerCase()
    .replace(/:00(?=[ap]m\b)/, '');
  return `${s} ET`;
}

/** datetime-local value (no Z) = Eastern wall clock */
export function isoToDateTimeLocalEastern(isoString) {
  if (!isoString) return '';
  const ms = toMillis(isoString);
  if (isNaN(ms)) return '';
  const dt = DateTime.fromMillis(ms, { zone: 'utc' }).setZone(ZNY);
  return dt.toFormat("yyyy-MM-dd'T'HH:mm");
}
